import {
  AffiliationPassive,
  CardElement,
  ColorRank,
  DigitalPassport,
  ElementHighlights,
  ElementStat,
  GameCard,
  PlayStyle,
  PvpStats,
} from "../types/battle"
import { AFFILIATION_ELEMENTS, resolveAffiliationPassive } from "../constants/affiliationInfo"
import {
  normalizeGuardianPathInfo as mapGuardianPathInfo,
} from "./factionNormalize"
import {
  defaultShardDust,
  normalizeCardShardSlots,
  normalizeShardDust,
  pickCardShardSlotsRaw,
} from "./shardsNormalize"
import { normalizeGuardianPath } from "../constants/guardianPaths"
import {
  normalizeActiveTimeProgress,
  normalizeGuardianRankInfo,
  normalizePetDbRecord,
  normalizeUnlockedCosmetics,
  pickGuardianRankFromRecord,
} from "./petNormalize"

type RawRecord = Record<string, unknown>

const CARD_ELEMENTS: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "LIGHTNING",
  "WIND",
  "ICE",
]

const PLAY_STYLES: PlayStyle[] = ["ATTACK", "DEFENSE", "SPEED"]

const COLOR_RANKS: ColorRank[] = [
  "WHITE",
  "GREEN",
  "BLUE",
  "PURPLE",
  "ORANGE",
  "RED",
]

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1") return true
  if (value === 0 || value === "0") return false
  return fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

function pickEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T
): T {
  const normalized = pickString(value).toUpperCase()
  return allowed.includes(normalized as T) ? (normalized as T) : fallback
}

function pickNullableNumber(value: unknown): number | null {
  if (value == null || value === "") return null
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export const EMPTY_PVP_STATS: PvpStats = {
  pvpWins: 0,
  pvpLosses: 0,
  pvpDraws: 0,
  pvpBattles: 0,
  winRate: null,
}

export function normalizePvpStats(raw: unknown): PvpStats {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...EMPTY_PVP_STATS }
  }
  const obj = raw as RawRecord
  const pvpWins = pickNumber(obj.pvpWins ?? obj.pvp_wins ?? obj.wins)
  const pvpLosses = pickNumber(obj.pvpLosses ?? obj.pvp_losses ?? obj.losses)
  const pvpDraws = pickNumber(obj.pvpDraws ?? obj.pvp_draws ?? obj.draws)
  const pvpBattles = pickNumber(
    obj.pvpBattles ?? obj.pvp_battles ?? obj.battles,
    pvpWins + pvpLosses + pvpDraws
  )
  const winRateRaw = pickNullableNumber(obj.winRate ?? obj.win_rate)
  return {
    pvpWins,
    pvpLosses,
    pvpDraws,
    pvpBattles,
    winRate: pvpBattles > 0 ? winRateRaw : null,
  }
}

export function defaultElementStats(): ElementStat[] {
  return AFFILIATION_ELEMENTS.map((element) => ({
    element,
    wins: 0,
    losses: 0,
    draws: 0,
    battles: 0,
    winRate: null,
  }))
}

function normalizeElementStat(raw: unknown): ElementStat | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const element = pickEnum(obj.element, CARD_ELEMENTS, "FIRE")
  const wins = pickNumber(obj.wins)
  const losses = pickNumber(obj.losses)
  const draws = pickNumber(obj.draws)
  const battles = pickNumber(obj.battles, wins + losses + draws)
  const winRateRaw = pickNullableNumber(obj.winRate ?? obj.win_rate)
  return {
    element,
    wins,
    losses,
    draws,
    battles,
    winRate: battles > 0 ? winRateRaw : null,
  }
}

export function normalizeElementStats(raw: unknown): ElementStat[] {
  const list = Array.isArray(raw) ? raw : []
  const byElement = new Map<CardElement, ElementStat>()
  for (const item of list) {
    const stat = normalizeElementStat(item)
    if (stat) byElement.set(stat.element, stat)
  }
  return AFFILIATION_ELEMENTS.map(
    (element) => byElement.get(element) ?? {
      element,
      wins: 0,
      losses: 0,
      draws: 0,
      battles: 0,
      winRate: null,
    }
  )
}

export function normalizeElementHighlights(raw: unknown): ElementHighlights {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {
      favoriteElement: null,
      bestElement: null,
      bestElementMinBattles: 5,
    }
  }
  const obj = raw as RawRecord
  const favoriteRaw = pickString(obj.favoriteElement ?? obj.favorite_element).toUpperCase()
  const bestRaw = pickString(obj.bestElement ?? obj.best_element).toUpperCase()
  return {
    favoriteElement: CARD_ELEMENTS.includes(favoriteRaw as CardElement)
      ? (favoriteRaw as CardElement)
      : null,
    bestElement: CARD_ELEMENTS.includes(bestRaw as CardElement)
      ? (bestRaw as CardElement)
      : null,
    bestElementMinBattles: pickNumber(
      obj.bestElementMinBattles ?? obj.best_element_min_battles,
      5
    ),
  }
}

export function normalizeAffiliationPassive(raw: unknown): AffiliationPassive | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const title = pickString(obj.title)
  const description = pickString(obj.description ?? obj.desc)
  if (!title && !description) return null
  return { title, description }
}

export function normalizeGameCard(raw: unknown): GameCard | null {
  if (!raw || typeof raw !== "object") return null
  const obj = raw as RawRecord
  const id = obj.id ?? obj.uuid ?? obj.cardId ?? obj.card_id
  if (id == null) return null

  return {
    id: String(id),
    user_id: pickNumber(obj.user_id ?? obj.userId),
    element: pickEnum(obj.element, CARD_ELEMENTS, "FIRE"),
    style: pickEnum(obj.style ?? obj.playStyle ?? obj.play_style, PLAY_STYLES, "ATTACK"),
    level: Math.max(1, pickNumber(obj.level, 1)),
    basePower: pickNumber(obj.basePower ?? obj.base_power ?? obj.power),
    colorRank: pickEnum(obj.colorRank ?? obj.color_rank ?? obj.rank, COLOR_RANKS, "WHITE"),
    isReborn: pickBool(obj.isReborn ?? obj.is_reborn),
    victories: pickNumber(obj.victories ?? obj.wins),
    totalBattles: pickNumber(obj.totalBattles ?? obj.total_battles ?? obj.battles),
    styleChangedAt:
      pickString(obj.styleChangedAt ?? obj.style_changed_at) || null,
    maxShardSlots:
      pickNumber(obj.maxShardSlots ?? obj.max_shard_slots) || undefined,
    shardSlots: normalizeCardShardSlots(pickCardShardSlotsRaw(obj)),
    sellPrice: (() => {
      const raw = obj.sellPrice ?? obj.sell_price
      if (raw == null || raw === "") return undefined
      const value = pickNumber(raw)
      return Number.isFinite(value) ? value : undefined
    })(),
    sortOrder: (() => {
      const raw = obj.sortOrder ?? obj.sort_order
      if (raw == null || raw === "") return undefined
      const value = pickNumber(raw)
      return Number.isFinite(value) ? value : undefined
    })(),
    storedInBank: pickBool(
      obj.storedInBank ?? obj.stored_in_bank ?? obj.inBank ?? obj.in_bank,
      false
    )
      ? true
      : undefined,
    listedOnMarket: pickBool(
      obj.listedOnMarket ??
        obj.listed_on_market ??
        obj.onMarket ??
        obj.on_market,
      false
    )
      ? true
      : undefined,
  }
}

export function normalizeCardWithStats(raw: unknown): GameCard | null {
  const card = normalizeGameCard(raw)
  if (!card) return null
  if (!raw || typeof raw !== "object") return card
  const obj = raw as RawRecord
  if (obj.stats == null) return card
  return { ...card, stats: normalizePvpStats(obj.stats) }
}

/** Извлекает массив сырых карточек из типичных обёрток бэка. */
function extractRawCardsList(data: unknown): unknown[] {
  if (Array.isArray(data)) return data

  if (!data || typeof data !== "object") return []

  const top = data as RawRecord
  const nestedTop = top.data ?? top.result ?? top.payload
  if (Array.isArray(nestedTop)) return nestedTop

  const obj = unwrapPayload(data)
  const candidates = [
    obj.cards,
    obj.items,
    obj.createdCards,
    obj.created_cards,
    obj.newCards,
    obj.new_cards,
  ]

  for (const list of candidates) {
    if (Array.isArray(list)) return list
  }

  const nested = obj.data ?? obj.result ?? obj.payload
  if (Array.isArray(nested)) return nested
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const inner = nested as RawRecord
    if (Array.isArray(inner.cards)) return inner.cards
    if (Array.isArray(inner.items)) return inner.items
  }

  return []
}

export function normalizeGameCardsList(data: unknown): GameCard[] {
  return extractRawCardsList(data)
    .map(normalizeCardWithStats)
    .filter((card): card is GameCard => card != null)
}

export function normalizeGameCards(data: unknown): GameCard[] {
  const cards = normalizeGameCardsList(data).filter(
    (card) => card.storedInBank !== true && card.listedOnMarket !== true
  )

  const hasSortOrder = cards.some((card) => card.sortOrder != null)
  if (!hasSortOrder) return cards

  return [...cards].sort((a, b) => {
    const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER
    const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER
    if (ao !== bo) return ao - bo
    return a.id.localeCompare(b.id)
  })
}

export function isSameGameCard(a: GameCard, b: GameCard): boolean {
  return (
    a.id === b.id &&
    a.user_id === b.user_id &&
    a.element === b.element &&
    a.style === b.style &&
    a.level === b.level &&
    a.basePower === b.basePower &&
    a.colorRank === b.colorRank &&
    a.isReborn === b.isReborn &&
    a.victories === b.victories &&
    a.totalBattles === b.totalBattles &&
    (a.styleChangedAt ?? null) === (b.styleChangedAt ?? null)
  )
}

export function isSameGameCardList(a: GameCard[], b: GameCard[]): boolean {
  if (a.length !== b.length) return false
  return a.every((card, index) => isSameGameCard(card, b[index]))
}

export function isSameDigitalPassport(
  a: DigitalPassport | null | undefined,
  b: DigitalPassport | null | undefined
): boolean {
  if (a === b) return true
  if (!a || !b) return false

  if (
    a.user_id !== b.user_id ||
    a.nickname !== b.nickname ||
    a.publicGameCode !== b.publicGameCode ||
    a.coins !== b.coins ||
    a.cardCapacity !== b.cardCapacity ||
    a.chakraCharges !== b.chakraCharges ||
    a.fuseBoosters !== b.fuseBoosters ||
    a.styleChangeTokens !== b.styleChangeTokens ||
    a.energy !== b.energy ||
    a.maxEnergy !== b.maxEnergy ||
    a.globalPts !== b.globalPts ||
    a.localPts !== b.localPts ||
    a.qrBattlesToday !== b.qrBattlesToday ||
    a.academicLeague !== b.academicLeague ||
    a.activeCardId !== b.activeCardId ||
    a.city_id !== b.city_id ||
    (a.city?.title ?? null) !== (b.city?.title ?? null) ||
    a.averageGrade !== b.averageGrade ||
    a.academicBoostActiveUntil !== b.academicBoostActiveUntil ||
    a.lastQuizTaken !== b.lastQuizTaken ||
    a.examBlockedUntil !== b.examBlockedUntil ||
    a.affiliationElement !== b.affiliationElement ||
    a.needsAffiliationSelection !== b.needsAffiliationSelection
  ) {
    return false
  }

  if (!a.activeCard && !b.activeCard) return true
  if (!a.activeCard || !b.activeCard) return false
  return isSameGameCard(a.activeCard, b.activeCard)
}

/**
 * Сливает частичный ответ API (install/uninstall shard и т.п.) с полной картой.
 * Без этого missing `element` в ответе нормализуется в FIRE и мигает в UI.
 * Передавайте `rawCard` — сырой объект карты из ответа; без него `updated` считается полным.
 */
export function mergeGameCardUpdate(
  previous: GameCard,
  updated: GameCard,
  rawCard?: unknown
): GameCard {
  if (rawCard == null) {
    return {
      ...previous,
      ...updated,
      maxShardSlots: updated.maxShardSlots ?? previous.maxShardSlots,
      shardSlots: updated.shardSlots ?? previous.shardSlots,
      stats: updated.stats ?? previous.stats,
      user_id: updated.user_id ?? previous.user_id,
    }
  }

  const raw =
    typeof rawCard === "object" && !Array.isArray(rawCard)
      ? (rawCard as RawRecord)
      : null

  const hasField = (...keys: string[]) =>
    raw != null &&
    keys.some((key) => {
      const value = raw[key]
      return value != null && String(value).trim() !== ""
    })

  const rawHasShards = raw != null && pickCardShardSlotsRaw(raw) != null

  return {
    ...previous,
    ...updated,
    element: hasField("element") ? updated.element : previous.element,
    style: hasField("style", "playStyle", "play_style")
      ? updated.style
      : previous.style,
    colorRank: hasField("colorRank", "color_rank", "rank")
      ? updated.colorRank
      : previous.colorRank,
    level: hasField("level") ? updated.level : previous.level,
    basePower: hasField("basePower", "base_power", "power")
      ? updated.basePower
      : previous.basePower,
    isReborn: hasField("isReborn", "is_reborn")
      ? updated.isReborn
      : previous.isReborn,
    victories: hasField("victories", "wins")
      ? updated.victories
      : previous.victories,
    totalBattles: hasField("totalBattles", "total_battles", "battles")
      ? updated.totalBattles
      : previous.totalBattles,
    styleChangedAt: hasField("styleChangedAt", "style_changed_at")
      ? updated.styleChangedAt
      : previous.styleChangedAt,
    maxShardSlots: updated.maxShardSlots ?? previous.maxShardSlots,
    shardSlots: rawHasShards || (updated.shardSlots?.length ?? 0) > 0
      ? updated.shardSlots
      : previous.shardSlots,
    stats: updated.stats ?? previous.stats,
    user_id: updated.user_id ?? previous.user_id,
  }
}

export function mergeGameCardIntoList(
  cards: GameCard[],
  updated: GameCard
): GameCard[] {
  const index = cards.findIndex((card) => card.id === updated.id)
  if (index < 0) return [...cards, updated]
  const next = cards.slice()
  next[index] = updated
  return next
}

/** Ответ POST spend-chakra: одна карта в корне, без passport. */
export function normalizeSingleCardResponse(
  data: unknown,
  preferredCardId?: string
): GameCard | null {
  const direct = normalizeGameCard(data)
  if (direct?.id) return direct

  const obj = unwrapPayload(data)
  const fromPayload = pickCardFromPayload(obj)
  if (fromPayload?.id) return fromPayload

  const cards = normalizeGameCards(obj)
  if (preferredCardId) {
    return cards.find((c) => c.id === preferredCardId) ?? cards[0] ?? null
  }
  return cards[0] ?? null
}

/** ID экипированной карты — только activeCardId (fallback на embed для legacy). */
export function resolveActiveCardId(
  passport: DigitalPassport | null | undefined
): string | null {
  const id =
    passport?.activeCardId?.trim() || passport?.activeCard?.id?.trim() || ""
  return id || null
}

/**
 * Синхронизирует activeCard с GET /game/cards.
 * Вложенный activeCard в /passport/me может быть устаревшим (другой level/rank).
 */
export function syncPassportActiveCard(
  passport: DigitalPassport,
  cards: GameCard[]
): DigitalPassport {
  const activeId = resolveActiveCardId(passport)
  if (!activeId) {
    return { ...passport, activeCardId: null, activeCard: null }
  }

  const fromList = cards.find((c) => c.id === activeId) ?? null
  if (fromList) {
    if (
      __DEV__ &&
      passport.activeCard &&
      (passport.activeCard.level !== fromList.level ||
        passport.activeCard.colorRank !== fromList.colorRank ||
        passport.activeCard.basePower !== fromList.basePower)
    ) {
      console.warn("[PASSPORT] activeCard из /passport/me устарел, берём /game/cards", {
        activeId,
        embed: {
          level: passport.activeCard.level,
          colorRank: passport.activeCard.colorRank,
          basePower: passport.activeCard.basePower,
        },
        list: {
          level: fromList.level,
          colorRank: fromList.colorRank,
          basePower: fromList.basePower,
        },
      })
    }
    return { ...passport, activeCardId: activeId, activeCard: fromList }
  }

  if (__DEV__) {
    console.warn(
      "[PASSPORT] activeCardId не найден в /game/cards, сбрасываем экипировку:",
      activeId
    )
  }
  return { ...passport, activeCardId: null, activeCard: null }
}

function resolveActiveCardRaw(record: RawRecord): unknown {
  const raw = record.activeCard ?? record.active_card
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw
  const obj = raw as RawRecord
  if (obj.id ?? obj.uuid ?? obj.cardId ?? obj.card_id) return raw
  const nested = obj.card ?? obj.gameCard ?? obj.game_card
  return nested ?? raw
}

function resolvePassportRecord(raw: unknown): RawRecord | null {
  if (!raw || typeof raw !== "object") return null
  const obj = unwrapPayload(raw)
  const source =
    obj.user_id != null ||
    obj.userId != null ||
    obj.nickname != null ||
    obj.publicGameCode != null ||
    obj.public_game_code != null
      ? obj
      : (obj.passport ?? obj.profile ?? obj.digitalPassport ?? obj.digital_passport)

  if (!source || typeof source !== "object" || Array.isArray(source)) return null
  return source as RawRecord
}

function fieldPresent(record: RawRecord, ...keys: string[]): boolean {
  return keys.some((key) => record[key] !== undefined && record[key] !== null)
}

/** Обновляет только поля, явно пришедшие с бэка — частичный ответ не обнуляет паспорт. */
export function mergeDigitalPassport(
  base: DigitalPassport,
  raw: unknown
): DigitalPassport {
  const record = resolvePassportRecord(raw)
  const parsed = normalizeDigitalPassport(raw)
  if (!parsed) return base
  if (!record) return parsed

  const next: DigitalPassport = { ...base }

  if (fieldPresent(record, "user_id", "userId")) next.user_id = parsed.user_id
  if (fieldPresent(record, "nickname", "name")) next.nickname = parsed.nickname
  if (
    fieldPresent(
      record,
      "publicGameCode",
      "public_game_code",
      "gameCode",
      "game_code"
    )
  ) {
    next.publicGameCode = parsed.publicGameCode
  }
  if (fieldPresent(record, "coins")) next.coins = parsed.coins
  if (fieldPresent(record, "shardDust", "shard_dust")) {
    next.shardDust = parsed.shardDust ?? defaultShardDust()
  }
  // Не берём голое `chakra` — в ответах actions часто cost/reward, а не баланс.
  if (fieldPresent(record, "chakraCharges", "chakra_charges", "charges")) {
    next.chakraCharges = parsed.chakraCharges
  }
  if (fieldPresent(record, "fuseBoosters", "fuse_boosters")) {
    next.fuseBoosters = parsed.fuseBoosters
  }
  if (
    fieldPresent(record, "styleChangeTokens", "style_change_tokens")
  ) {
    next.styleChangeTokens = parsed.styleChangeTokens
  }
  if (fieldPresent(record, "energy")) next.energy = parsed.energy
  if (fieldPresent(record, "maxEnergy", "max_energy")) {
    next.maxEnergy = parsed.maxEnergy
  }
  if (fieldPresent(record, "globalPts", "global_pts", "points")) {
    next.globalPts = parsed.globalPts
  }
  if (fieldPresent(record, "localPts", "local_pts")) {
    next.localPts = parsed.localPts
  }
  if (fieldPresent(record, "city_id", "cityId")) next.city_id = parsed.city_id
  if (fieldPresent(record, "qrBattlesToday", "qr_battles_today")) {
    next.qrBattlesToday = parsed.qrBattlesToday
  }
  if (
    fieldPresent(record, "academicLeague", "academic_league", "league")
  ) {
    next.academicLeague = parsed.academicLeague
  }
  if (fieldPresent(record, "averageGrade", "average_grade")) {
    next.averageGrade = parsed.averageGrade
  }
  if (
    fieldPresent(
      record,
      "academicBoostActiveUntil",
      "academic_boost_active_until"
    )
  ) {
    next.academicBoostActiveUntil = parsed.academicBoostActiveUntil
  }
  if (fieldPresent(record, "lastQuizTaken", "last_quiz_taken")) {
    next.lastQuizTaken = parsed.lastQuizTaken
  }
  if (fieldPresent(record, "examBlockedUntil", "exam_blocked_until")) {
    next.examBlockedUntil = parsed.examBlockedUntil
  }
  if (fieldPresent(record, "activeCardId", "active_card_id")) {
    next.activeCardId = parsed.activeCardId
  }
  if (fieldPresent(record, "activeCard", "active_card")) {
    next.activeCard = parsed.activeCard
  }
  if (fieldPresent(record, "city")) next.city = parsed.city
  if (fieldPresent(record, "createAt", "created_at")) {
    next.createAt = parsed.createAt
  }
  if (fieldPresent(record, "updateAt", "updated_at")) {
    next.updateAt = parsed.updateAt
  }
  if (fieldPresent(record, "visibleOnMap", "visible_on_map")) {
    next.visibleOnMap = parsed.visibleOnMap
  }
  // Стихию обновляем только если она явно пришла — иначе slim DTO с pvpStats
  // сбрасывал affiliation и открывал экран выбора стихии.
  if (
    fieldPresent(
      record,
      "affiliationElement",
      "affiliation_element",
      "needsAffiliationSelection",
      "needs_affiliation_selection",
      "affiliationPassive",
      "affiliation_passive"
    )
  ) {
    next.affiliationElement = parsed.affiliationElement
    next.affiliationPassive = parsed.affiliationPassive
    next.needsAffiliationSelection = parsed.needsAffiliationSelection
  }
  if (
    fieldPresent(
      record,
      "pvpStats",
      "pvp_stats",
      "deckPvpStats",
      "deck_pvp_stats",
      "elementStats",
      "element_stats",
      "elementHighlights",
      "element_highlights"
    )
  ) {
    if (fieldPresent(record, "pvpStats", "pvp_stats")) {
      next.pvpStats = parsed.pvpStats
    }
    if (fieldPresent(record, "deckPvpStats", "deck_pvp_stats")) {
      next.deckPvpStats = parsed.deckPvpStats
    }
    if (fieldPresent(record, "elementStats", "element_stats")) {
      next.elementStats = parsed.elementStats
    }
    if (fieldPresent(record, "elementHighlights", "element_highlights")) {
      next.elementHighlights = parsed.elementHighlights
    }
  }
  if (
    fieldPresent(
      record,
      "guardianPath",
      "guardian_path",
      "guardianPathInfo",
      "guardian_path_info",
      "needsGuardianPathSelection",
      "needs_guardian_path_selection",
      "profileFrameKey",
      "profile_frame_key",
      "avatarVariant",
      "avatar_variant",
      "minorityBonusUntil",
      "minority_bonus_until"
    )
  ) {
    next.guardianPath = parsed.guardianPath
    next.guardianPathInfo = parsed.guardianPathInfo
    next.needsGuardianPathSelection = parsed.needsGuardianPathSelection
    next.profileFrameKey = parsed.profileFrameKey
    next.avatarVariant = parsed.avatarVariant
    next.minorityBonusUntil = parsed.minorityBonusUntil
  }
  if (
    fieldPresent(
      record,
      "activeTime",
      "active_time",
      "guardianRankInfo",
      "guardian_rank_info",
      "guardianRank",
      "guardian_rank",
      "spiritAwakened",
      "spirit_awakened",
      "canAwakenPet",
      "can_awaken_pet",
      "canAwaken",
      "can_awaken",
      "preAwakenPassiveMult",
      "pre_awaken_passive_mult",
      "pet",
      "unlockedCosmetics",
      "unlocked_cosmetics",
      "ownedCardCosmetics",
      "owned_card_cosmetics",
      "equippedCardBgKey",
      "equipped_card_bg_key",
      "equippedNickColorKey",
      "equipped_nick_color_key",
      "equippedNickPulseKey",
      "equipped_nick_pulse_key"
    )
  ) {
    next.activeTime = parsed.activeTime
    next.guardianRankInfo = parsed.guardianRankInfo
    next.spiritAwakened = parsed.spiritAwakened
    next.canAwakenPet = parsed.canAwakenPet
    next.preAwakenPassiveMult = parsed.preAwakenPassiveMult
    next.pet = parsed.pet
    next.unlockedCosmetics = parsed.unlockedCosmetics
    next.ownedCardCosmetics = parsed.ownedCardCosmetics
    next.equippedCardBgKey = parsed.equippedCardBgKey
    next.equippedNickColorKey = parsed.equippedNickColorKey
    next.equippedNickPulseKey = parsed.equippedNickPulseKey
  }

  return next
}

export function normalizeDigitalPassport(raw: unknown): DigitalPassport | null {
  const record = resolvePassportRecord(raw)
  if (!record) return null

  const activeCardRaw = resolveActiveCardRaw(record)
  const activeCard =
    activeCardRaw && typeof activeCardRaw === "object"
      ? normalizeGameCard(activeCardRaw)
      : null

  const cityRaw = record.city
  const city =
    cityRaw && typeof cityRaw === "object"
      ? {
          city_id: pickNumber((cityRaw as RawRecord).city_id ?? (cityRaw as RawRecord).id),
          title: pickString((cityRaw as RawRecord).title ?? (cityRaw as RawRecord).name),
        }
      : null

  const affiliationRaw = pickString(
    record.affiliationElement ?? record.affiliation_element
  ).toUpperCase()
  const affiliationElement = CARD_ELEMENTS.includes(affiliationRaw as CardElement)
    ? (affiliationRaw as CardElement)
    : null

  const needsAffiliationSelectionExplicit = record.needsAffiliationSelection ??
    record.needs_affiliation_selection
  const needsAffiliationSelection =
    needsAffiliationSelectionExplicit != null
      ? pickBool(needsAffiliationSelectionExplicit)
      : !affiliationElement &&
        (record.academicLeague != null ||
          record.academic_league != null ||
          record.league != null)

  const guardianPath = normalizeGuardianPath(
    record.guardianPath ?? record.guardian_path
  )
  const needsGuardianPathSelectionExplicit =
    record.needsGuardianPathSelection ?? record.needs_guardian_path_selection
  const needsGuardianPathSelection =
    needsGuardianPathSelectionExplicit != null
      ? pickBool(needsGuardianPathSelectionExplicit)
      : !guardianPath &&
        (record.academicLeague != null ||
          record.academic_league != null ||
          record.league != null)

  return {
    user_id: pickNumber(record.user_id ?? record.userId),
    nickname: pickString(record.nickname ?? record.name, "Хранитель"),
    nicknameRename: (() => {
      const raw = record.nicknameRename ?? record.nickname_rename
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
      const obj = raw as Record<string, unknown>
      return {
        canRename: pickBool(obj.canRename ?? obj.can_rename, true),
        nextRenameAt:
          pickString(obj.nextRenameAt ?? obj.next_rename_at) || null,
        daysLeft: Math.max(0, pickNumber(obj.daysLeft ?? obj.days_left)),
      }
    })(),
    publicGameCode: pickString(
      record.publicGameCode ?? record.public_game_code ?? record.gameCode ?? record.game_code
    ),
    coins: pickNumber(record.coins),
    cardCapacity: Math.max(
      1,
      pickNumber(
        record.cardCapacity ?? record.card_capacity ?? record.maxCards ?? record.max_cards,
        30
      )
    ),
    chakraCharges: pickNumber(
      record.chakraCharges ?? record.chakra_charges ?? record.charges ?? record.chakra
    ),
    fuseBoosters: pickNumber(record.fuseBoosters ?? record.fuse_boosters),
    styleChangeTokens: pickNumber(
      record.styleChangeTokens ?? record.style_change_tokens
    ),
    energy: pickNumber(record.energy),
    maxEnergy: Math.max(1, pickNumber(record.maxEnergy ?? record.max_energy, 100)),
    globalPts: pickNumber(record.globalPts ?? record.global_pts ?? record.points),
    localPts: pickNumber(record.localPts ?? record.local_pts),
    city_id: pickNumber(record.city_id ?? record.cityId) || null,
    academicLeague: (() => {
      const league = pickString(
        record.academicLeague ?? record.academic_league ?? record.league
      ).toUpperCase()
      return league === "TITAN" || league === "ATLANT"
        ? (league as DigitalPassport["academicLeague"])
        : null
    })(),
    averageGrade:
      record.averageGrade != null || record.average_grade != null
        ? pickNumber(record.averageGrade ?? record.average_grade)
        : null,
    academicBoostActiveUntil:
      pickString(record.academicBoostActiveUntil ?? record.academic_boost_active_until) ||
      null,
    lastQuizTaken: pickString(record.lastQuizTaken ?? record.last_quiz_taken) || undefined,
    examBlockedUntil:
      pickString(record.examBlockedUntil ?? record.exam_blocked_until) || null,
    activeCardId:
      pickString(record.activeCardId ?? record.active_card_id ?? activeCard?.id) || null,
    qrBattlesToday: pickNumber(record.qrBattlesToday ?? record.qr_battles_today),
    activeCard,
    city,
    affiliationElement,
    avatarVariant: (() => {
      const raw = record.avatarVariant ?? record.avatar_variant
      if (raw == null || raw === "") return 1
      const n = Math.floor(pickNumber(raw, 1))
      return n >= 1 && n <= 3 ? n : 1
    })(),
    affiliationPassive: resolveAffiliationPassive(
      affiliationElement,
      normalizeAffiliationPassive(
        record.affiliationPassive ?? record.affiliation_passive
      )
    ),
    needsAffiliationSelection,
    guardianPath,
    guardianPathInfo: mapGuardianPathInfo(
      record.guardianPathInfo ?? record.guardian_path_info,
      guardianPath
    ),
    needsGuardianPathSelection,
    profileFrameKey:
      pickString(record.profileFrameKey ?? record.profile_frame_key) || null,
    minorityBonusUntil:
      pickString(record.minorityBonusUntil ?? record.minority_bonus_until) ||
      null,
    pvpStats: normalizePvpStats(record.pvpStats ?? record.pvp_stats),
    deckPvpStats: normalizePvpStats(
      record.deckPvpStats ?? record.deck_pvp_stats
    ),
    elementStats: normalizeElementStats(record.elementStats ?? record.element_stats),
    elementHighlights: normalizeElementHighlights(
      record.elementHighlights ?? record.element_highlights
    ),
    createAt: pickString(record.createAt ?? record.created_at) || undefined,
    updateAt: pickString(record.updateAt ?? record.updated_at) || undefined,
    visibleOnMap: pickBool(record.visibleOnMap ?? record.visible_on_map, true),
    shardDust: normalizeShardDust(record.shardDust ?? record.shard_dust),
    activeTime: normalizeActiveTimeProgress(
      record.activeTime ?? record.active_time
    ),
    guardianRankInfo: normalizeGuardianRankInfo(
      record.guardianRankInfo ??
        record.guardian_rank_info ??
        record.guardianRank ??
        record.guardian_rank
    ),
    guardianRank: pickGuardianRankFromRecord(record),
    spiritAwakened: pickBool(
      record.spiritAwakened ?? record.spirit_awakened
    )
      ? true
      : record.spiritAwakened != null || record.spirit_awakened != null
        ? false
        : undefined,
    canAwakenPet: pickBool(
      record.canAwakenPet ??
        record.can_awaken_pet ??
        record.canAwaken ??
        record.can_awaken
    )
      ? true
      : record.canAwakenPet != null ||
          record.can_awaken_pet != null ||
          record.canAwaken != null ||
          record.can_awaken != null
        ? false
        : undefined,
    preAwakenPassiveMult:
      record.preAwakenPassiveMult != null ||
      record.pre_awaken_passive_mult != null
        ? pickNumber(
            record.preAwakenPassiveMult ?? record.pre_awaken_passive_mult,
            0.5
          )
        : undefined,
    pet: normalizePetDbRecord(record.pet),
    unlockedCosmetics: normalizeUnlockedCosmetics(
      record.unlockedCosmetics ?? record.unlocked_cosmetics
    ),
    ownedCardCosmetics: (() => {
      const raw =
        record.ownedCardCosmetics ?? record.owned_card_cosmetics
      if (!Array.isArray(raw)) return []
      return raw.filter((x): x is string => typeof x === "string")
    })(),
    equippedCardBgKey:
      pickString(
        record.equippedCardBgKey ?? record.equipped_card_bg_key
      ) || null,
    equippedNickColorKey:
      pickString(
        record.equippedNickColorKey ?? record.equipped_nick_color_key
      ) || null,
    equippedNickPulseKey:
      pickString(
        record.equippedNickPulseKey ?? record.equipped_nick_pulse_key
      ) || null,
    guild: (() => {
      const g = record.guild
      if (!g || typeof g !== "object" || Array.isArray(g)) return null
      const rec = g as RawRecord
      const tag = pickString(rec.tag)
      const name = pickString(rec.name)
      if (!tag && !name) return null
      return {
        guildId: pickString(rec.guildId ?? rec.guild_id),
        tag,
        name,
        emblemKey: pickString(rec.emblemKey ?? rec.emblem_key, "gild_1"),
        level: pickNumber(rec.level, 1),
      }
    })(),
  }
}

function pickCardFromPayload(obj: RawRecord): GameCard | null {
  const candidates = [
    obj.card,
    obj.updatedCard,
    obj.updated_card,
    obj.gameCard,
    obj.game_card,
    obj.mainCard,
    obj.main_card,
    obj.resultCard,
    obj.result_card,
    obj.fusedCard,
    obj.fused_card,
    obj.targetCard,
    obj.target_card,
  ]

  for (const raw of candidates) {
    const card = normalizeGameCard(raw)
    if (card) return card
  }

  return null
}

export function normalizeCardActionResponse(
  data: unknown,
  options?: { preferredCardId?: string }
): {
  passport: DigitalPassport | null
  card: GameCard | null
} {
  const obj = unwrapPayload(data)
  const passport = normalizeDigitalPassport(
    obj.passport ?? obj.profile ?? obj.digitalPassport ?? obj.digital_passport ?? obj
  )

  let card = pickCardFromPayload(obj)

  if (!card && options?.preferredCardId) {
    const cards = normalizeGameCards(obj)
    card = cards.find((c) => c.id === options.preferredCardId) ?? null
  }

  if (!card && options?.preferredCardId && passport?.activeCard?.id === options.preferredCardId) {
    card = passport.activeCard
  }

  return { passport, card }
}

export function normalizePassportActionResponse(data: unknown): {
  passport: DigitalPassport | null
  cards: GameCard[]
} {
  const obj = unwrapPayload(data)
  const passport = normalizeDigitalPassport(
    obj.passport ?? obj.profile ?? obj.digitalPassport ?? obj.digital_passport ?? obj
  )
  const cards = normalizeGameCards(obj)
  return { passport, cards }
}
